// Steps:  upload -> validating -> review -> creating -> done
//   upload     : template download + drag/drop / browse (.xlsx only)
//   validating : API call chal rahi hai
//   review     : errors (Create disabled) ya success summary (Create enabled)
//   creating   : progress indicator
//   done       : total created + result file download

import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { IoMdClose } from 'react-icons/io';
import {
  FiDownload,
  FiUploadCloud,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
} from 'react-icons/fi';
import './BulkUploadModal.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // backend multer limit ke saath same

const STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'review', label: 'Validate' },
  { key: 'done', label: 'Create' },
];

const getToken = () => {
  try {
    const raw = sessionStorage.getItem('tokens') || sessionStorage.getItem('token') || '';
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const t = parsed.token || parsed.accessToken || parsed.access_token || '';
        if (typeof t === 'string' && t.trim()) return t.trim().replace(/^Bearer\s+/i, '');
      }
    } catch (e) {
      console.warn('Failed to parse token', e);
    }
    return String(raw).trim().replace(/^Bearer\s+/i, '');
  } catch (e) {
    console.warn('Failed to retrieve token', e);
    return '';
  }
};

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const base64ToBlob = (base64, mime) => {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const BulkUploadModal = ({ closeModal, onCompleted }) => {
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingMasters, setDownloadingMasters] = useState(false);

  const [validation, setValidation] = useState(null);
  const [createResult, setCreateResult] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  // creation ke beech modal band nahi hona chahiye
  const safeClose = () => {
    if (step === 'validating' || step === 'creating') return;
    if (step === 'done' && typeof onCompleted === 'function') onCompleted();
    closeModal();
  };

  /* ---------------- template download ---------------- */
  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/officer/bulk-upload/template`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      saveBlob(res.data, 'Seeds_Bulk_User_Upload_Template.xlsx');
    } catch (err) {
      toast.error('Failed to download sample file');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadMastersReference = async () => {
    setDownloadingMasters(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/officer/bulk-upload/masters-reference`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      saveBlob(res.data, 'Seeds_Masters_Reference.xlsx');
    } catch (err) {
      toast.error('Failed to download masters data');
    } finally {
      setDownloadingMasters(false);
    }
  };

  /* ---------------- file pick ---------------- */
  const pickFile = (f) => {
    setFileError('');
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      setFile(null);
      setFileError('Only .xlsx files are accepted. Please upload the filled sample file.');
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setFile(null);
      setFileError('File is too large. Maximum size is 5 MB.');
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const resetToUpload = () => {
    setFile(null);
    setFileError('');
    setValidation(null);
    setStep('upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ---------------- validate ---------------- */
  const handleValidate = async () => {
    if (!file) return;
    setStep('validating');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/officer/bulk-upload/validate`, formData, {
        headers: authHeaders(),
      });
      setValidation(res.data?.data);
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Validation failed. Please try again.');
      setStep('upload');
    }
  };

  /* ---------------- create ---------------- */
  const handleCreate = async () => {
    setStep('creating');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/officer/bulk-upload/create`, formData, {
        headers: authHeaders(),
        timeout: 0, // Timeout disabled: bcrypt hashing for 200 users may take significant time
      });
      const data = res.data?.data;

      if (data && data.created === false) {
        // validation ke beech me kuch change hua -> review pe wapas
        setValidation(data.validation);
        setStep('review');
        toast.error('Data changed since validation. Please fix the errors and re-upload.');
        return;
      }

      setCreateResult(data);
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk creation failed');
      setStep('review');
    }
  };

  const handleDownloadResult = () => {
    if (!createResult?.result_file) return;
    saveBlob(
      base64ToBlob(createResult.result_file.base64, XLSX_MIME),
      createResult.result_file.file_name
    );
  };

  /* ---------------- render helpers ---------------- */
  const activeStepIndex = step === 'upload' || step === 'validating' ? 0 : step === 'review' ? 1 : 2;

  const renderUpload = () => (
    <>
      <p className="bu-desc">
        Download the sample Excel file, fill in user details, and upload to create multiple users at once.
      </p>

      <div className="bu-download-card" onClick={handleDownloadTemplate} role="button" tabIndex={0}>
        <div className="bu-download-icon"><FiFileText /></div>
        <div>
          <div className="bu-download-title">Seeds_Bulk_User_Upload_Template.xlsx</div>
          <div className="bu-download-sub">Field Officers · Admin Panel Users · Reference</div>
        </div>
        <span className="bu-download-btn">
          <FiDownload /> {downloading ? 'Downloading...' : 'Download Sample File'}
        </span>
      </div>

      <div className="bu-download-card" onClick={handleDownloadMastersReference} role="button" tabIndex={0}>
        <div className="bu-download-icon"><FiFileText /></div>
        <div>
          <div className="bu-download-title">Seeds_Masters_Reference.xlsx</div>
          <div className="bu-download-sub">All Role IDs · All Branch IDs</div>
        </div>
        <span className="bu-download-btn">
          <FiDownload /> {downloadingMasters ? 'Downloading...' : 'Download Masters Data'}
        </span>
      </div>

      {!file ? (
        <div
          className={`bu-dropzone ${dragOver ? 'bu-dropzone-active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <FiUploadCloud className="bu-dropzone-icon" />
          <div className="bu-dropzone-title">Drag & drop your file here, or click to browse</div>
          <div className="bu-dropzone-sub">Only .xlsx files · max 5 MB</div>
        </div>
      ) : (
        <div className="bu-file">
          <div className="bu-file-icon"><FiFileText /></div>
          <div>
            <div className="bu-file-name">{file.name}</div>
            <div className="bu-file-size">{(file.size / 1024).toFixed(1)} KB</div>
          </div>
          <button type="button" className="bu-file-remove" onClick={resetToUpload} aria-label="Remove file">
            <IoMdClose />
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={(e) => pickFile(e.target.files?.[0])}
      />

      {fileError && <div className="bu-inline-error"><FiAlertCircle /> {fileError}</div>}
    </>
  );

  const renderReview = () => {
    if (!validation) return null;
    const { is_valid, summary, errors = [], warnings = [] } = validation;

    return (
      <>
        {is_valid ? (
          <div className="bu-success-banner">
            <FiCheckCircle />
            <div>
              <strong>
                {summary.total} users ready to create — {summary.field_officers} field officers,{' '}
                {summary.web_users} admin panel users.
              </strong>
              <div>Proceed?</div>
            </div>
          </div>
        ) : (
          <div className="bu-error-banner">
            <FiAlertCircle />
            <div>
              <strong>
                {summary.error_count} errors found{summary.error_rows ? ` across ${summary.error_rows} rows` : ''}.
              </strong>
              <div>Fix the errors below and re-upload.</div>
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div className="bu-table-wrap">
            <table className="bu-table">
              <thead>
                <tr><th>Sheet</th><th>Row</th><th>Column</th><th>Error</th></tr>
              </thead>
              <tbody>
                {errors.map((e, i) => (
                  <tr key={`e-${e.sheet}-${e.row}-${e.column}-${i}`}>
                    <td>{e.sheet}</td>
                    <td>{e.row ?? '-'}</td>
                    <td>{e.column ?? '-'}</td>
                    <td className="bu-msg-error">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {warnings.length > 0 && (
          <>
            <div className="bu-warning-title">Warnings ({warnings.length}) — these will not block creation</div>
            <div className="bu-table-wrap bu-table-wrap-small">
              <table className="bu-table">
                <tbody>
                  {warnings.map((w, i) => (
                    <tr key={`w-${w.sheet}-${w.row}-${i}`}>
                      <td>{w.sheet}</td>
                      <td>{w.row}</td>
                      <td className="bu-msg-warning">{w.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );
  };

  const renderBusy = (text) => (
    <div className="bu-busy">
      <div className="bu-progress"><div className="bu-progress-bar" /></div>
      <p>{text}</p>
    </div>
  );

  const renderDone = () => {
    const s = createResult?.summary || { total: 0, created: 0, failed: 0 };
    return (
      <div className="bu-done">
        <FiCheckCircle className="bu-done-icon" />
        <h4>{s.created} of {s.total} users created</h4>
        {s.failed > 0 && (
          <p className="bu-msg-error">{s.failed} rows failed. Check the result file for reasons.</p>
        )}
        <button type="button" className="bu-btn bu-btn-outline" onClick={handleDownloadResult}>
          <FiDownload /> Download Result File
        </button>
      </div>
    );
  };

  /* ---------------- footer ---------------- */
  const renderFooter = () => {
    if (step === 'upload') {
      return (
        <>
          <button type="button" className="bu-btn bu-btn-outline" onClick={safeClose}>Cancel</button>
          <button type="button" className="bu-btn bu-btn-primary" disabled={!file} onClick={handleValidate}>
            Validate File
          </button>
        </>
      );
    }
    if (step === 'review') {
      return (
        <>
          <button type="button" className="bu-btn bu-btn-outline" onClick={resetToUpload}>Re-upload</button>
          <button
            type="button"
            className="bu-btn bu-btn-primary"
            disabled={!validation?.is_valid}
            onClick={handleCreate}
          >
            Create Users
          </button>
        </>
      );
    }
    if (step === 'done') {
      return <button type="button" className="bu-btn bu-btn-primary" onClick={safeClose}>Close</button>;
    }
    return null; // validating / creating me buttons nahi
  };

  return (
    <div>
      <div className="bu-overlay" onClick={safeClose}></div>

      <div className="bu-modal">
        <div className="bu-header">
          <h5>Bulk Upload Users</h5>
          <button type="button" className="bu-close" onClick={safeClose} aria-label="Close">
            <IoMdClose />
          </button>
        </div>

        <div className="bu-steps">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={`bu-step ${i === activeStepIndex ? 'bu-step-active' : ''} ${i < activeStepIndex ? 'bu-step-done' : ''}`}
            >
              <span className="bu-step-dot">{i + 1}</span> {s.label}
            </div>
          ))}
        </div>

        <div className="bu-body">
          {step === 'upload' && renderUpload()}
          {step === 'validating' && renderBusy('Validating file... no users are created at this stage.')}
          {step === 'review' && renderReview()}
          {step === 'creating' && renderBusy('Creating users... please do not close this window.')}
          {step === 'done' && renderDone()}
        </div>

        <div className="bu-footer">{renderFooter()}</div>
      </div>
    </div>
  );
};

export default BulkUploadModal;